import { ActionButton, Button, Plus, Snippet, toast } from '@highmobility/console-ui-components';
import { FC, Fragment, useState } from 'react';
import { format } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { sort } from 'radash';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  createMQTTV2Certificate,
  fakeLoader,
  fetchMQTTV2Certificates,
} from '@/services/apiService';
import { downloadZipFile } from '@/services/textFileService';
import { MQTTV2Certificate, ProjectInstance } from '@/types';
import { RevokeMQTTV2CertificateDialog } from '@/components/organisms/RevokeMQTTV2CertificateDialog';
import { usePermissions } from '@/hooks/usePermissions';

import { CertificateSnippetDialog } from './CertificateSnippetDialog';

export type MQTTV2CerificatesSectionProps = {
  teamId: string;
  projectId: string;
  instance: ProjectInstance;
  certificates: MQTTV2Certificate[];
};

const ALLOWED_DATES_FOR_REVOCATION = new Date(2026, 1, 17);

export const MQTTV2CerificatesSection: FC<MQTTV2CerificatesSectionProps> = observer((props) => {
  const { userHasPermission } = usePermissions(props.teamId);
  const hasManagePermission = userHasPermission(`projects.manage.${props.instance}.credentials`);
  const [showSnippetDialog, setShowSnippetDialog] = useState(false);
  const [selectedCertificateId, setSelectedCertificateId] = useState<string>();
  const [revokeDialog, setRevokeDialog] = useState<{
    isOpen: boolean;
    certificateId: string | null;
  }>({
    isOpen: false,
    certificateId: null,
  });

  const certificateInstance = props.instance === 'production' ? 'live' : 'sandbox';
  const certificateSnippet = `mosquitto_sub --host mqtt-v2.high-mobility.com --port 8883 --qos 1 \\
--topic '$share/${props.projectId}/${certificateInstance}/level13/${props.projectId}' \\
--id ${props.projectId}-${certificateInstance}-random-id --disable-clean-session \\
--cert client_crt.pem --key client_key.pem   --cafile ca_crt.pem \\
--verbose --debug`;

  const certificatesQuery = useQuery({
    queryKey: ['mqttV2Certificates', props.teamId, props.projectId, props.instance],
    queryFn: async () => {
      try {
        const { data } = await fetchMQTTV2Certificates(
          props.teamId,
          props.projectId,
          props.instance
        );
        return sort(data, (item) => new Date(item.not_before).getTime(), true);
      } catch (e) {
        console.error('MQTTV2CertificatesSection::certificatesQuery failed', e);
        toast('Could not fetch certificates.', 'error');
        throw e;
      }
    },
    initialData: props.certificates,
    refetchInterval: (query) => {
      /* istanbul ignore next */
      const existingData = query.state.data ?? [];
      if (existingData.some((c) => ['creating', 'revoking'].includes(c.status))) {
        return 1500;
      }

      return false;
    },
    staleTime: 2000,
  });

  const createMQTTV2CertificateMutation = useMutation({
    mutationFn: async () => {
      const [{ data }] = await Promise.all([
        createMQTTV2Certificate(props.teamId, props.projectId, props.instance),
        fakeLoader(),
      ]);

      return data;
    },
    onError: (e: any) => {
      console.error('createMQTTV2Certificate::failed to create certificate', e);
      toast(
        e?.response?.status < 500
          ? e?.response?.data?.message
          : 'Something went wrong. Please try again.',
        'error'
      );
    },
    onSuccess: (data) => {
      downloadZipFile(
        `${props.projectId}-${certificateInstance}.zip`,
        [
          {
            fileName: 'ca_crt.pem',
            contents: data.ca_crt_pem,
          },
          {
            fileName: 'client_crt.pem',
            contents: data.client_crt_pem,
          },
          {
            fileName: 'client_key.pem',
            contents: data.client_key_pem,
          },
          {
            fileName: 'snippet.txt',
            contents: certificateSnippet,
          },
        ],
        {
          toastMessage: 'Certificate saved to your computer. Store it securely.',
        }
      );

      certificatesQuery.refetch();
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <h5 className="text-heading-5">Add MQTT V2 Client Certificate</h5>
      <ActionButton
        aria-label="Add MQTT Client certificate"
        className="w-full"
        disabled={!hasManagePermission || createMQTTV2CertificateMutation.isPending}
        onClick={() => createMQTTV2CertificateMutation.mutate()}
      >
        {(cn) => <Plus className={cn} />}
      </ActionButton>
      <div
        className="grid gap-4 whitespace-nowrap"
        style={{
          gridTemplateColumns:
            '280px 1fr minmax(125px, 1fr) minmax(125px, 1fr) max-content max-content',
        }}
      >
        {certificatesQuery.data.map((certificate, i) => (
          <Fragment key={i}>
            <div className="">
              <div className="">Serial</div>
              <div className="text-body-small">{certificate.serial}</div>
            </div>
            <div className="">
              <div className="">Status</div>
              <div className="text-body-small">{certificate.status}</div>
            </div>
            <div>
              <div className="">Valid from</div>
              <div className="text-body-small">
                {format(new Date(certificate.not_before), 'yyyy-MM-dd hh:mm:ss')}
              </div>
            </div>
            <div>
              <div className="">Expiration date</div>
              <div className="text-body-small">
                {format(new Date(certificate.not_after), 'yyyy-MM-dd hh:mm:ss')}
              </div>
            </div>
            <ActionButton
              className="border-0"
              size="small"
              onClick={() => {
                setSelectedCertificateId(certificate.id);
                setShowSnippetDialog(true);
              }}
              aria-label="View certificate snippet"
            >
              {(cn) => <Snippet className={cn} />}
            </ActionButton>
            {new Date(certificate.not_before) >= ALLOWED_DATES_FOR_REVOCATION &&
            certificate.status !== 'revoked' ? (
              <Button
                intent="secondary"
                disabled={!hasManagePermission}
                onClick={() =>
                  setRevokeDialog({
                    isOpen: true,
                    certificateId: certificate.id,
                  })
                }
              >
                Revoke
              </Button>
            ) : (
              <div></div>
            )}
          </Fragment>
        ))}
      </div>
      {selectedCertificateId && (
        <CertificateSnippetDialog
          isOpen={showSnippetDialog}
          setIsOpen={setShowSnippetDialog}
          snippet={certificateSnippet}
        />
      )}

      {revokeDialog.certificateId && (
        <RevokeMQTTV2CertificateDialog
          isOpen={revokeDialog.isOpen}
          setIsOpen={() => setRevokeDialog({ isOpen: false, certificateId: null })}
          teamId={props.teamId}
          projectId={props.projectId}
          instance={props.instance}
          certificateId={revokeDialog.certificateId}
          onSuccess={async () => {
            await certificatesQuery.refetch();
            toast(`Certificate revoked`, 'success');
            setRevokeDialog({ isOpen: false, certificateId: null });
          }}
        />
      )}
    </div>
  );
});
